import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

// Single, reusable renderer for one blend ingredient. Reads the structured
// `composition: [{name, amount, slug}]` field on the blend product record
// (see BLEND_COMPOSITION in backend/seed/seed_data.py, that's the source
// of truth). If the ingredient's slug maps to a currently-live, purchasable
// product, we render a real crawlable <a href> to the base compound PDP.
// Otherwise the chip renders as static text, so withdrawn / discontinued
// ingredients never become dead links.
//
// The chip's visual style is a verbatim copy of the previous static pill
// (border, background, mono type, padding) so switching to a link produces
// zero visual change; only a subtle underline-less hover state is added
// to hint interactivity.
const CHIP_CLASS =
  "inline-flex items-center rounded-full border border-brandblue/25 bg-brandblue/8 px-3 py-1 font-mono text-[12px] font-medium tracking-[0.04em] text-brandblue";

export default function BlendIngredientChip({ ingredient }) {
  // React Query cache is shared: CartDrawer, CatalogPage and this chip all
  // key on ["products-all"], so this call is deduped and served from
  // cache the vast majority of the time. Falls back to `enabled: !!slug`
  // so plain-text chips never trigger the fetch.
  const slug = ingredient?.slug || null;
  const { data: liveProducts } = useQuery({
    queryKey: ["products-all"],
    queryFn: async () => (await api.get("/products")).data,
    staleTime: 5 * 60 * 1000,
    enabled: !!slug,
  });

  if (!ingredient) return null;

  const label = `${ingredient.name} ${ingredient.amount || ""}`.trim();
  const testIdSuffix = ingredient.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  // Ingredient product is considered clickable only when we've confirmed
  // it exists in the live catalog AND is not hidden/coming-soon/accessory.
  // While the products list is loading, we render as plain text, better
  // than briefly showing a link that then de-activates on hydration.
  const live = Array.isArray(liveProducts)
    ? liveProducts.find(
        (p) =>
          p.slug === slug &&
          !p.hidden &&
          !p.coming_soon &&
          !p.is_accessory,
      )
    : null;

  if (slug && live) {
    return (
      <Link
        to={`/product/${slug}`}
        className={`${CHIP_CLASS} transition-colors hover:bg-brandblue/16 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brandblue`}
        data-testid={`blend-ingredient-link-${testIdSuffix}`}
      >
        {label}
      </Link>
    );
  }

  return (
    <span
      className={CHIP_CLASS}
      data-testid={`blend-ingredient-static-${testIdSuffix}`}
    >
      {label}
    </span>
  );
}
